<svelte:options accessors/>
<script lang="ts">
    import NauiButton from "../naui/atoms/NauiButton.svelte";
    import LicItem from "./components/LicItem.svelte";
    import NauiCheckbox from "../naui/atoms/NauiCheckbox.svelte";
    import NauiFloatingAction from "../naui/components/NauiFloatingAction.svelte";
    import {createEventDispatcher} from "svelte";
    import ItemsClipboard from "./components/ItemsClipboard.svelte";
    import type {ILicItem} from "../interfaces/lic-item.interface";
    import {utils, read, WorkBook} from "xlsx";
    import slugify from "slugify";
    import {itemMapper, validateColumns} from "./utils";


    export let list: Array<ILicItem> = [];
    export let enableCommercial = false;
    export let editable: boolean = false;
    export let canFinalize: boolean = false;
    export let templateURL: string = null;
    export let modal;

    const dispatch = createEventDispatcher();
    let elements = [];
    let inputFile;

    export let openClipboard = false;

    const columns: Array<any> = [
        {
            label: 'Grupo',
            required: false,
            type: String
        },
        {
            label: 'Item',
            required: false,
            type: String
        },
        {
            label: 'Código institucional',
            required: false,
            type: String
        }, {
            label: 'Descripción producto',
            required: true,
            type: String
        },
        {
            label: 'Cantidad solicitada',
            required: false,
            type: Number
        },
        {
            label: 'Precio Techo',
            required: false,
            type: Number
        },
    ];
    columns.forEach(c => {
        c.slug = slugify(c.label, {
            lower: true,
            trim: true,
            strict: true
        });
    });

    function addItem() {
        list.push({
            number: list.length + 1,
            group: undefined,
            code: undefined,
            name: undefined,
            quantity: undefined,
            price: undefined
        });
        list = list;
    }

    function remove(index) {
        list.splice(index, 1);
        list.forEach((item: any, index: number) => {
            item.number = index + 1;
        });
        list = list;
    }

    function getData() {
        return {
            enableCommercial,
            list
        }
    }

    function loadData(event) {
        openClipboard = false;
        const {items} = event.detail;
        list = [];
        items.forEach((item: any) => {
            list.push({
                ...item
            });
        })
        list = list;
    }

    function getDataFromXlsx(file: File): Promise<Array<any>> {
        return new Promise<Array<any>>((resolve, reject) => {
            try {
                const reader = new FileReader()
                reader.onloadend = (e: any) => {
                    const w: WorkBook = read(e.target.result, {
                        type: 'buffer'
                    });
                    const data = (utils.sheet_to_json(w.Sheets[w.SheetNames[0]])).map((item: any) => {
                        const obj = {};
                        Object.keys(item).forEach((key: string) => {
                            obj[slugify(key, {
                                lower: true,
                                trim: true
                            })] = item[key];
                        });
                        return obj;
                    });
                    resolve(data);
                };
                reader.readAsArrayBuffer(file);
            } catch (e) {
                reject(e);
            }
        })
    }

    async function loadFromTemplate(evt: any): Promise<void> {
        const fileList = (evt.target.files as FileList);
        let file: File;
        if (fileList && fileList.length > 0) {
            file = fileList[0];
        }
        if (file) {
            getDataFromXlsx(file).then((data: Array<any>) => {
                const {raw, valid, errors} = validateColumns(data, columns, itemMapper);
                if (!valid) {
                    const code = `<code>` + errors.join(`<br>`) + `</code>`
                    const message = `<div class="mb-1"><p>Carga masiva no valida.</p></div><div class="mb-1 logs text-l">${code}</div>`;
                    modal.openModal({
                        closeButton: false,
                        closeOnEsc: true,
                        closeOnOuterClick: true,
                        message,
                        button: 'VOLVER'
                    });
                } else {
                    list = [];
                    list = [...raw];
                }
            }).catch((reason) => {
                modal.openModal({
                    closeButton: false,
                    closeOnEsc: true,
                    closeOnOuterClick: true,
                    message: `<p>Carga masiva no valida.</p>`,
                    button: 'VOLVER'
                });
                console.error(reason);
            });
        }
    }
</script>
<section>
    <fieldset>
        <legend>Cargue de items solicitados por el cliente</legend>
        {#if editable}
            <div class="box-r sx-1">
                {#if list.length > 0}
                    <div class="radius py-ii px-1 back-gray">
                        Items cargados: <strong>{list.length}</strong>
                    </div>
                {/if}
                <NauiButton mode="stroked"
                            caption="COPIAR DESDE PORTAPEPELES"
                            on:click={() => openClipboard = true}/>
                <NauiButton mode="stroked"
                            caption="CARGA MASIVA"
                            on:click={inputFile.click()}/>
            </div>
        {/if}
        <table class="naui-table w-100 mt-15">
            <thead>
            <tr>
                <th style="width: 5%">
                    <div>Item</div>
                </th>
                <th style="width: 10%">
                    <div>Grupo</div>
                </th>
                <th style="width: 10%">
                    <div>Código cliente</div>
                </th>
                <th>
                    <div>Nombre solicitado por el cliente</div>
                </th>
                <th style="width: 10%">
                    <div>Cantidad solicitada</div>
                </th>
                <th style="width: 10%">
                    <div>Precio techo pliego</div>
                </th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            {#each list as item, i}
                <LicItem bind:data={item}
                         on:remove={remove.bind(this, i)}
                         isDisabled={!editable && !!item.slug}/>
            {:else}
                <tr>
                    <td colspan="7">
                        <div>
                            <p>No se han encontrado registros</p>
                        </div>
                    </td>
                </tr>
            {/each}
            </tbody>
        </table>
        <div class="mt-1 mb-15">
            {#if editable}
                <NauiButton caption="AÑADIR ITEM"
                            icon="plus"
                            color="accent" on:click={addItem}/>
            {/if}
        </div>
        {#if editable && canFinalize}
            <NauiCheckbox label="Habilitar carga de items a los asesores comerciales"
                          bind:value={enableCommercial}/>
        {:else if enableCommercial}
            <NauiButton caption="AÑADIR ITEM"
                        icon="plus"
                        color="accent" on:click={addItem}/>
        {/if}

        {#if editable && canFinalize}
            <div class="box-l sx-ii mt-1">
                <NauiButton caption="REGRESAR"
                            color="accent"
                            mode="outline"
                            on:click={()=>dispatch('back', getData())}/>
                <NauiButton caption="FINALIZAR"
                            color="accent"
                            mode="solid"
                            on:click={()=>dispatch('finish', getData())}/>
            </div>
        {/if}
    </fieldset>
</section>
<NauiFloatingAction>
    {#if editable}
        <NauiButton caption="GUARDAR"
                    icon="save"
                    color="accent"
                    mode="solid"
                    on:click={()=>dispatch('save', getData())}/>
    {:else if enableCommercial }

        <NauiButton caption="GUARDAR"
                    icon="save"
                    color="accent"
                    mode="solid"
                    on:click={()=>dispatch('save', getData())}/>
    {/if}
</NauiFloatingAction>

{#if editable}
    <ItemsClipboard open={openClipboard}
                    {templateURL}
                    {columns}
                    on:close={()=>openClipboard = false}
                    on:load={loadData}/>
{/if}

<input type="file"
       class="d-none"
       on:change={loadFromTemplate}
       bind:this={inputFile}>